import React, {
    ButtonHTMLAttributes,
    AnchorHTMLAttributes,
    ReactElement,
    forwardRef,
    ForwardedRef,
    useState,
} from 'react';
import { IconLock, IconChevron } from '../../assets/icons';
import './Button.css';

/**
    This component returns an anchor element or button element based on whether or not an `href` property is passed in.
**/
export interface IButtonProps {
    variant?:
    | 'solid'
    | 'ghosted-light'
    | 'ghosted-dark'
    | 'inactive'
    | 'ghosted-inactive'
    | 'link'
    | 'link-light'
    | 'chip';
    size?: 'small' | 'medium' | 'large';
    icon?: 'lock' | 'chevron' | 'back' | 'arrowDown' | undefined;
    target?: TAnchorTarget;
    disableOnClick?: boolean;
    onClick?: THandlerFunction;
    disabled?: boolean;
    'aria-disabled'?: boolean;
    children?: string | null | ReactElement;
}

const IconType = ({
    icon,
    size,
}: {
    icon?: IButtonProps['icon'];
    size?: IButtonProps['size'];
}) => {
    const iconClass = `icon--${icon}`;
    if (icon === 'lock') return <IconLock className={iconClass} size={size} />;
    return <IconChevron className={iconClass} size={size} />;
};

const ButtonContent = ({ icon, size, children }: Record<string, any>) => {
    const chev = icon && <IconType icon={icon} size={size} />;
    if (icon === 'lock' || icon === 'back') {
        return (
            <>
                {chev}
                {children}
            </>
        );
    }
    return (
        <>
            {children}
            {chev}
        </>
    );
};

const buttonVariantClasses = {
    'solid': 'solid bg-blue-creditone border-blue-creditone text-white',
    'ghosted-light': 'ghosted-light bg-transparent border-white text-white',
    'ghosted-dark': 'ghosted-dark bg-transparent border-blue-creditone text-blue-creditone',
    'inactive': 'inactive bg-grey-chalice border-grey-chalice !text-white cursor-not-allowed',
    'ghosted-inactive': 'ghosted-inactive bg-transparent border-grey-chalice text-grey-chalice cursor-not-allowed',
    'link': 'link bg-transparent border-transparent text-blue-creditone',
    'link-light': 'link-light bg-transparent border-transparent text-white opacity-100',
    'chip': 'chip text-grey-tundora bg-transparent box-border',
};

const Button = forwardRef(
    (
        {
            disableOnClick = false,
            variant = 'solid',
            children,
            className = '',
            size = 'large',
            icon = undefined,
            disabled = false,
            href = '',
            target = '_self',
            onClick,
            ...rest
        }: IButtonProps & AnchorHTMLAttributes<any> & ButtonHTMLAttributes<any>,
        ref: ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
    ) => {

        const getButtonStyles = () => {
            const isDisabled = disabled || isDisabledFromClick;
            let buttonStyles = '';
            switch (variant) {
                case 'solid':
                    buttonStyles = isDisabled ? buttonVariantClasses['inactive'] : buttonVariantClasses[variant];
                    break;
                case 'ghosted-dark':
                case 'ghosted-light':
                    buttonStyles = isDisabled ? buttonVariantClasses['ghosted-inactive'] : buttonVariantClasses[variant];
                    break;
                case 'inactive':
                case 'ghosted-inactive':
                case 'link-light':
                case 'chip':
                case 'link':
                    buttonStyles = buttonVariantClasses[variant];
                    break;
            }
            return buttonStyles;
        };

        const [isDisabledFromClick, setIsDisabledFromClick] = useState(false);
        const formattedClassName = `inline-flex border-solid border-2 items-center justify-center text-center Button ${getButtonStyles()} ${size} ${className}`;
        const content = (
            <ButtonContent icon={icon} children={children} size={size} />
        );
        const onClickHandler = (event) => {
            if (typeof onClick === 'function') {
                onClick(event);
            }
            if (disableOnClick && event.target.nodeName === "BUTTON") {
                setIsDisabledFromClick(true);
            }
        }

        if (href) {
            const anchorProps: AnchorHTMLAttributes<any> = {
                href,
                target,
                className: formattedClassName,
                ...rest,
            };
            return (
                <a {...anchorProps} onClick={onClickHandler} ref={ref as React.Ref<HTMLAnchorElement>} data-cl={1}>
                    {content}
                </a>
            );
        } else {
            const buttonProps: ButtonHTMLAttributes<any> = {
                type: 'button',
                disabled: isDisabledFromClick ? isDisabledFromClick : disabled,
                className: formattedClassName,
                ...rest,
            };
            return (
                <button role='button' {...buttonProps} onClick={onClickHandler} ref={ref as React.Ref<HTMLButtonElement>} data-cl={1}>
                    {content}
                </button>
            );
        }
    },
);

Button.displayName = 'Button';

export default Button;
